import { Component, OnInit, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { BarChartComparisonComponent } from '../../shared/components/bar-chart-comparison/bar-chart-comparison.component';
import { ScoreCardsComponent } from '../../shared/components/score-cards/score-cards.component';
import { firstValueFrom } from 'rxjs';
import { simplifiedAreaComparison } from '../../shared/sample-education/simplified-area-comparison';
import { aiCoursesSearch } from '../../shared/sample-education/ai-courses-search';
import { aiCoursesCompare } from '../../shared/sample-education/ai-courses-compare';
import { aiCoursesRecommendations } from '../../shared/sample-education/ai-courses-recommendations';

import {
  BrandIndexService,
  CourseSearchParams,
  CourseSearchResponse,
  CourseData,
  CourseComparisonParams,
  CourseComparisonResponse,
  CourseRecommendationParams,
  CourseRecommendationResponse
} from '../../services/brand-index.service';

@Component({
  selector: 'app-ai-course-finder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatIconModule,
    MatDividerModule,
    MatSnackBarModule,
    BarChartComparisonComponent,
    ScoreCardsComponent
  ],
  templateUrl: './ai-course-finder.component.html',
  styleUrls: ['./ai-course-finder.component.scss']
})
export class AICourseFinderComponent implements OnInit {
  searchForm: FormGroup;
  recommendationForm: FormGroup;
  comparisonForm: FormGroup;

  // Search results
  searchResults: CourseData[] = [];
  totalFound = 0;
  isLoading = false;

  // Comparison results
  comparisonResults: CourseComparisonResponse | null = null;

  // Recommendation results
  recommendations: CourseRecommendationResponse | null = null;

  // Visualization options for comparison
  comparisonVisualizationType: 'table' | 'bar' | 'cards' = 'table';
  simplifiedComparisonData: any = null;
  isComparisonDataLoading = false;

  // View mode for search results
  viewMode: 'list' | 'cards' = 'list';

  // Enhanced tuition fees data
  tuitionFeesData: any[] = [];
  isTuitionFeesLoading = false;

  // Filter options - removed categories since we're using a single search input

  experienceLevels = [
    'Beginner',
    'Intermediate',
    'Advanced',
    'Professional'
  ];

  budgetRanges = [
    'Under £5k',
    '£5k-£15k',
    '£15k-£30k',
    '£30k-£50k',
    'Over £50k'
  ];

  countries = ['UK', 'US', 'Canada', 'Australia', 'Germany', 'Netherlands', 'Global'];

  constructor(
    private fb: FormBuilder,
    private brandIndexService: BrandIndexService,
    private snackBar: MatSnackBar
  ) {
    this.searchForm = this.fb.group({
      query: ['MBA'],
      category: ['Business'],
      country: ['UK'],
      experience_level: [''],
      budget_range: [''],
      limit: [10]
    });

    this.recommendationForm = this.fb.group({
      interests: ['MBA'],
      experience_level: ['Intermediate'],
      budget_range: ['£5k-£15k'],
      preferred_location: ['UK'],
      limit: [5]
    });

    this.comparisonForm = this.fb.group({
      universities: ['University of Hull, Warwick Business School'],
      course_type: ['MBA']
    });
  }

  ngOnInit(): void {
    // Load initial data if needed
    // Load default sample data for all tabs
    this.loadDefaultSampleData();
  }

  // Load default sample data for all tabs
  loadDefaultSampleData(): void {
    // Load search sample data
    this.searchResults = aiCoursesSearch.courses as any;
    this.totalFound = aiCoursesSearch.search_summary.total_courses_found;

    // Load comparison sample data
    this.comparisonResults = aiCoursesCompare as any;

    // Load recommendations sample data
    this.recommendations = aiCoursesRecommendations as any;

    // Initialize simplified comparison data with sample data
    this.simplifiedComparisonData = simplifiedAreaComparison;
  }

  getCurrencyAmountFromStr(amount: any): number {
    if (!amount) return 0;
    if (typeof amount === 'number') return amount;
    const num = parseFloat(amount.replace(/[£,]/g, ''));
    return isNaN(num) ? 0 : num;
  }

  // Search AI courses
  searchCourses(): void {
    if (this.searchForm.valid) {
      this.isLoading = true;
      const params: CourseSearchParams = this.searchForm.value;

      this.brandIndexService.searchAICourses(params).subscribe({
        next: (response: CourseSearchResponse) => {
          this.searchResults = response.courses;
          this.totalFound = response.search_summary?.total_courses_found || response.courses?.length || 0;
          this.isLoading = false;

          if (this.totalFound === 0) {
            this.snackBar.open('No courses found matching your criteria. Try adjusting your filters.', 'Close', {
              duration: 5000
            });
          }
        },
        error: (error) => {
          this.isLoading = false;
          this.handleError(error, 'Failed to search courses');
        }
      });
    }
  }

  // Compare courses
  compareCourses(): void {
    if (this.comparisonForm.valid) {
      this.isLoading = true;
      const formValue = this.comparisonForm.value;

      // Convert universities string to array
      const universities = formValue.universities
        .split(',')
        .map((uni: string) => uni.trim())
        .filter((uni: string) => uni.length > 0);

      const params = {
        universities: universities,
        course_type: formValue.course_type
      };

      this.brandIndexService.compareAICourses(params).subscribe({
        next: (response: CourseComparisonResponse) => {
          this.comparisonResults = response;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.handleError(error, 'Failed to compare courses');
        }
      });
    }
  }

  // Get recommendations
  getRecommendations(): void {
    if (this.recommendationForm.valid) {
      this.isLoading = true;
      const params: CourseRecommendationParams = this.recommendationForm.value;

      this.brandIndexService.getAICourseRecommendations(params).subscribe({
        next: (response: CourseRecommendationResponse) => {
          this.recommendations = response;
          this.isLoading = false;
        },
        error: (error) => {
          this.isLoading = false;
          this.handleError(error, 'Failed to get recommendations');
        }
      });
    }
  }

  // Clear search results
  clearSearch(): void {
    // Reload default sample data
    this.searchResults = aiCoursesSearch.courses as any;
    this.totalFound = aiCoursesSearch.search_summary.total_courses_found;
    this.searchForm.reset({
      query: 'MBA',
      category: 'Business',
      country: 'UK',
      limit: 10
    });
  }

  // Clear comparison results
  clearComparison(): void {
    // Reload default sample data
    this.comparisonResults = aiCoursesCompare as any;
    this.comparisonForm.reset({
      universities: 'University of Hull, Warwick Business School',
      course_type: 'MBA'
    });
  }

  // Clear recommendations
  clearRecommendations(): void {
    // Reload default sample data
    this.recommendations = aiCoursesRecommendations as any;
    this.recommendationForm.reset({
      interests: 'MBA',
      experience_level: 'Intermediate',
      budget_range: '£5k-£15k',
      preferred_location: 'UK',
      limit: 5
    });
  }

  // Handle errors
  private handleError(error: any, defaultMessage: string): void {
    let message = defaultMessage;

    if (error.error?.detail) {
      message = error.error.detail;
    } else if (error.status === 503) {
      message = 'Service temporarily unavailable. Please try again later.';
    } else if (error.status === 404) {
      message = 'No courses found. Please check your search criteria.';
    } else if (error.status === 422) {
      message = 'Invalid request parameters. Please check your input.';
    }

    this.snackBar.open(message, 'Close', {
      duration: 5000
    });
  }

  // Get satisfaction color based on score
  getSatisfactionColor(score: number): string {
    if (score >= 90) return 'green';
    if (score >= 80) return 'orange';
    return 'red';
  }

  // Get rank color
  getRankColor(rank: string): string {
    if (rank.includes('1') || rank.includes('Elite')) return 'gold';
    if (rank.includes('2') || rank.includes('Top')) return 'silver';
    if (rank.includes('3') || rank.includes('Good')) return 'bronze';
    return 'gray';
  }

  // Switch comparison visualization type
  switchComparisonVisualization(type: 'table' | 'bar' | 'cards'): void {
    this.comparisonVisualizationType = type;

    // Load simplified data for alternative visualizations
    if (type !== 'table' && this.comparisonResults) {
      this.loadSimplifiedComparisonData();
    }
  }

  // Load simplified comparison data for bar/cards visualizations
  loadSimplifiedComparisonData(): void {
    if (!this.comparisonResults) return;

    this.isComparisonDataLoading = true;

    // Extract university names from comparison results
    const universities = Object.keys(this.comparisonResults.comparison);

    const payload = {
      universities: universities.slice(0, 5), // Limit to 5 universities
      category: this.comparisonForm.get('course_type')?.value || 'MBA',
      country: 'UK', // Default to UK for student mode
      models: ['chatgpt']
    };

    this.brandIndexService.getSimplifiedAreaComparison(payload).subscribe({
      next: (response) => {
        this.simplifiedComparisonData = response;
        this.isComparisonDataLoading = false;
      },
      error: (err) => {
        console.error('Failed to load simplified comparison data from API, using sample data:', err);
        // Fallback to sample data
        this.simplifiedComparisonData = simplifiedAreaComparison;
        this.isComparisonDataLoading = false;
        // console.log('Using simplified area comparison sample data:', simplifiedAreaComparison);
      }
    });
  }

  // Get metrics for visualizations
  getMetricsForVisualization(): string[] {
    if (this.simplifiedComparisonData?.metrics) {
      return this.simplifiedComparisonData.metrics;
    }

    // Default education metrics
    return [
      'academic_reputation',
      'faculty_quality',
      'program_structure',
      'admission_selectivity',
      'tuition_costs',
      'financial_aid',
      'career_outcomes',
      'student_life',
      'industry_connections',
      'international_support'
    ];
  }

  // Set view mode for search results
  setViewMode(mode: 'list' | 'cards'): void {
    this.viewMode = mode;
  }

  // Get fees category for color coding
  getFeesCategory(fees: number | string | undefined): string {
    if (!fees || fees === 'N/A') return 'unknown';

    const feeValue = typeof fees === 'string' ? parseInt(fees.replace(/[^0-9]/g, '')) : fees;

    if (feeValue >= 50000) return 'expensive';
    if (feeValue >= 30000) return 'moderate';
    return 'affordable';
  }

  // Convert ranking string to number for comparisons
  getRankingNumber(ranking: string | number | undefined): number {
    if (typeof ranking === 'number') return ranking;
    if (!ranking) return 999;

    // Extract number from ranking string (e.g., "Rank: 24" -> 24)
    const match = ranking.toString().match(/\d+/);
    return match ? parseInt(match[0]) : 999;
  }

  // Helper method to get employment rate as number
  getEmploymentRate(careerOutcomes: any): number {
    if (!careerOutcomes?.employment_rate) return 0;
    const rate = careerOutcomes.employment_rate;
    if (typeof rate === 'number') return rate;
    if (typeof rate === 'string') {
      const num = parseFloat(rate.replace('%', ''));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  // Helper method to get average salary as number
  getAverageSalary(careerOutcomes: any): number {
    if (!careerOutcomes?.average_salary) return 0;
    const salary = careerOutcomes.average_salary;
    if (typeof salary === 'number') return salary;
    if (typeof salary === 'string') {
      const num = parseFloat(salary.replace(/[£,]/g, ''));
      return isNaN(num) ? 0 : num;
    }
    return 0;
  }

  // Helper method to get student satisfaction score
  getStudentSatisfaction(course: any): number {
    return course?.student_satisfaction?.overall_satisfaction || 0;
  }

  // Helper method to get research rating
  getResearchRating(course: any): number {
    return course?.research_strength?.research_rating || 0;
  }

  // Helper method to get course name for display
  getCourseDisplayName(course: any): string {
    if (typeof course === 'string') return course;
    if (typeof course === 'object' && course.course_name) return course.course_name;
    return 'Unknown Course';
  }

  // ===== ENHANCED TUITION FEES METHODS =====

  /**
   * Load enhanced tuition fees for a university
   */
  async loadEnhancedTuitionFees(universityName: string): Promise<void> {
    if (!universityName) return;

    this.isTuitionFeesLoading = true;
    try {
      const response = await firstValueFrom(this.brandIndexService.searchTuitionFees({
        university_name: universityName
      }));

      if (response) {
        this.tuitionFeesData = response.fees || [];
        // console.log('Enhanced tuition fees loaded:', response);
      }
    } catch (error) {
      console.error('Error loading enhanced tuition fees:', error);
    } finally {
      this.isTuitionFeesLoading = false;
    }
  }

  /**
   * Get tuition fees for a specific university and course type
   */
  async getTuitionFeesForUniversity(universityName: string, courseType: string = 'all'): Promise<any[]> {
    if (!universityName) return [];

    try {
      const response = await firstValueFrom(this.brandIndexService.searchTuitionFees({
        university_name: universityName,
        course_type: courseType
      }));

      return response?.fees || [];
    } catch (error) {
      console.error('Error getting tuition fees:', error);
      return [];
    }
  }

  /**
   * Enhanced search with tuition fees integration
   */
  async searchCoursesWithEnhancedFees(): Promise<void> {
    this.isLoading = true;
    try {
      // Perform regular search
      await this.searchCourses();

      // Load enhanced tuition fees for each university in results
      for (const course of this.searchResults) {
        if (course.university_name) {
          const fees = await this.getTuitionFeesForUniversity(course.university_name);
          if (fees.length > 0) {
            // Update course with real tuition fees
            course.tuition_fees = fees[0].fee_amount + ' ' + fees[0].currency;
            course.fee_confidence = fees[0].confidence_score;
            course.fee_source = fees[0].source_url;
          }
        }
      }

      // console.log('Enhanced search with tuition fees completed');
    } catch (error) {
      console.error('Error in enhanced search:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Get confidence level for tuition fees
   */
  getFeeConfidenceLevel(confidence: number): string {
    if (confidence >= 0.8) return 'high';
    if (confidence >= 0.6) return 'medium';
    return 'low';
  }

  /**
   * Format tuition fees for display
   */
  formatTuitionFees(fees: any[]): string {
    if (!fees || fees.length === 0) return 'N/A';

    const fee = fees[0];
    return `${fee.fee_amount} ${fee.currency} (${fee.duration})`;
  }
}
